import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { 
  Bot, 
  Bug, 
  Lightbulb, 
  HelpCircle, 
  CheckCircle2, 
  XCircle, 
  Clock,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Target,
  Sparkles,
  ExternalLink,
  RefreshCw,
  Send,
  Edit3,
  Trash2,
  Link2,
  Plus,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  'Bug': { color: 'bg-red-100 text-red-800 border-red-200', icon: Bug },
  'Feature': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Lightbulb },
  'Question': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: HelpCircle },
};

const statusConfig = {
  'Open': { color: 'bg-gray-100 text-gray-800', icon: Clock },
  'Drafted': { color: 'bg-yellow-100 text-yellow-800', icon: Bot },
  'In Progress': { color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  'Approved': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  'Resolved': { color: 'bg-teal-100 text-teal-800', icon: CheckCircle2 },
  'Rejected': { color: 'bg-red-100 text-red-800', icon: XCircle },
};

const priorityConfig = {
  'Critical': { color: 'bg-red-500 text-white', icon: AlertTriangle },
  'High': { color: 'bg-orange-100 text-orange-800', icon: ArrowUp },
  'Medium': { color: 'bg-blue-100 text-blue-800', icon: Target },
  'Low': { color: 'bg-gray-100 text-gray-800', icon: ArrowDown },
};

export default function AISupportDashboard() {
  const { authToken } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [agentStats, setAgentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [linkedRoadmap, setLinkedRoadmap] = useState(null);
  const [editedResponse, setEditedResponse] = useState('');
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filter, setFilter] = useState({ type: 'all', status: 'all', priority: 'all' });
  
  // New ticket state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });

  useEffect(() => {
    if (authToken) {
      fetchTickets();
      fetchAgentStats();
    }
  }, [filter, authToken]);

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/support-agent/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          subject: newTicket.subject,
          message: newTicket.message
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Ticket created! AI classified as: ${data.ai_classification?.type || 'Unknown'}`);
        setIsCreateOpen(false);
        setNewTicket({ subject: '', message: '' });
        fetchTickets();
        fetchAgentStats();
      } else {
        toast.error('Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchTickets = async () => {
    if (!authToken) return;
    
    try {
      let url = `${API_URL}/api/support-agent/tickets?`;
      if (filter.type !== 'all') url += `ticket_type=${filter.type}&`;
      if (filter.status !== 'all') url += `status=${filter.status}&`;
      if (filter.priority !== 'all') url += `priority=${filter.priority}&`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await response.json();
      setTickets(data.tickets || []);
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentStats = async () => {
    if (!authToken) return;
    
    try {
      const response = await fetch(`${API_URL}/api/support-agent/stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await response.json();
      setAgentStats(data);
    } catch (error) {
      console.error('Failed to fetch agent stats:', error);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await fetch(`${API_URL}/api/support-agent/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await response.json();
      setSelectedTicket(data.ticket);
      setLinkedRoadmap(data.linked_roadmap);
      setEditedResponse(data.ticket.ai_draft_response || '');
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleApprove = async (roadmapStatus = null) => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${API_URL}/api/support-agent/tickets/${selectedTicket.id}/approve`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}` 
        },
        body: JSON.stringify({
          edited_response: editedResponse,
          update_roadmap_status: roadmapStatus
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Show appropriate toast based on actual status
        if (roadmapStatus === 'Planned') {
          toast.success('Ticket approved! Feature is now In Progress.');
        } else {
          toast.success('Ticket approved and resolved!');
        }
        setIsDetailOpen(false);
        fetchTickets();
        fetchAgentStats();
      }
    } catch (error) {
      toast.error('Failed to approve ticket');
    }
  };

  const handleReject = async () => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${API_URL}/api/support-agent/tickets/${selectedTicket.id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast.success('AI response rejected');
        setIsDetailOpen(false);
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleKillRoadmap = async (itemId) => {
    if (!window.confirm('Are you sure you want to reject this AI-generated roadmap item?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/support-agent/roadmap/${itemId}/kill`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast.success('Roadmap item rejected');
        setLinkedRoadmap(null);
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to reject roadmap item');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${API_URL}/api/support-agent/tickets/${selectedTicket.id}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Ticket status changed to ${newStatus}`);
        setSelectedTicket({ ...selectedTicket, status: newStatus });
        fetchTickets();
        fetchAgentStats();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div data-testid="ai-support-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-6 border-b border-slate-200 bg-white">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="h-6 w-6 text-purple-600" />
            AI Support Agent
          </h1>
          <p className="text-gray-600 mt-1">
            AI-powered ticket classification, response drafting, and roadmap automation
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => { fetchTickets(); fetchAgentStats(); }} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateOpen(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Main Content with Padding */}
      <div className="p-8 space-y-6">
        {/* AI Agent Stats */}
        {agentStats && (
        <div className="grid grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Tickets</div>
              <div className="text-2xl font-bold">{agentStats.total_tickets}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Pending Review</div>
              <div className="text-2xl font-bold">{agentStats.pending_review}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Resolved Today</div>
              <div className="text-2xl font-bold">{agentStats.resolved_today}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">AI Accuracy</div>
              <div className="text-2xl font-bold">{agentStats.ai_response_accuracy}%</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Auto-Roadmap Items</div>
              <div className="text-2xl font-bold">{agentStats.ai_generated_roadmap_items}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Bug">Bug</SelectItem>
            <SelectItem value="Feature">Feature</SelectItem>
            <SelectItem value="Question">Question</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Open">Open</SelectItem>
            <SelectItem value="Drafted">Drafted</SelectItem>
            <SelectItem value="In Progress">In Progress</SelectItem>
            <SelectItem value="Resolved">Resolved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filter.priority} onValueChange={(v) => setFilter({ ...filter, priority: v })}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="Critical">Critical</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
        
        {stats && (
          <div className="ml-auto text-sm text-gray-500">
            {stats.total} tickets total
          </div>
        )}
      </div>

      {/* Ticket List */}
      <div className="space-y-3">
        {tickets.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Bot className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No tickets found</p>
              <p className="text-sm">Tickets will appear here when users submit support requests</p>
            </CardContent>
          </Card>
        ) : (
          tickets.map((ticket) => {
            const TypeIcon = typeConfig[ticket.type]?.icon || HelpCircle;
            const StatusIcon = statusConfig[ticket.status]?.icon || Clock;
            const PriorityIcon = priorityConfig[ticket.priority]?.icon || Target;
            
            return (
              <Card 
                key={ticket.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewTicket(ticket.id)}
                data-testid={`ticket-${ticket.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={typeConfig[ticket.type]?.color}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {ticket.type}
                        </Badge>
                        <Badge className={priorityConfig[ticket.priority]?.color}>
                          <PriorityIcon className="h-3 w-3 mr-1" />
                          {ticket.priority}
                        </Badge>
                        <Badge className={statusConfig[ticket.status]?.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {ticket.status}
                        </Badge>
                        {ticket.linked_roadmap_id && (
                          <Badge variant="outline" className="text-purple-600 border-purple-200">
                            <Link2 className="h-3 w-3 mr-1" />
                            Roadmap Linked
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-gray-900">{ticket.subject}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{ticket.message}</p>
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span>{ticket.user_email || 'Anonymous'}</span>
                        <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        {ticket.similar_ticket_count > 0 && (
                          <span className="text-orange-500">+{ticket.similar_ticket_count} similar</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      {ticket.status === 'Drafted' && (
                        <div className="flex items-center gap-1 text-yellow-600 text-sm">
                          <Bot className="h-4 w-4" />
                          <span>AI Response Ready</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Ticket Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {typeConfig[selectedTicket.type]?.icon && 
                    React.createElement(typeConfig[selectedTicket.type].icon, { className: "h-5 w-5" })}
                  {selectedTicket.subject}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Badges */}
                <div className="flex gap-2 flex-wrap">
                  <Badge className={typeConfig[selectedTicket.type]?.color}>
                    {selectedTicket.type}
                  </Badge>
                  <Badge className={priorityConfig[selectedTicket.priority]?.color}>
                    {selectedTicket.priority} Priority
                  </Badge>
                  <Badge className={statusConfig[selectedTicket.status]?.color}>
                    {selectedTicket.status}
                  </Badge>
                </div>
                
                {/* User Message */}
                <div>
                  <label className="text-sm font-medium text-gray-700">User Message</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">
                    {selectedTicket.message}
                  </div>
                  <div className="mt-1 text-xs text-gray-400">
                    From: {selectedTicket.user_email || 'Anonymous'} | {new Date(selectedTicket.created_at).toLocaleString()}
                  </div>
                </div>
                
                {/* AI Classification */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 text-purple-700 font-medium mb-2">
                    <Sparkles className="h-4 w-4" />
                    AI Classification
                  </div>
                  <div className="text-sm text-purple-600">
                    <p><strong>Reasoning:</strong> {selectedTicket.ai_reasoning}</p>
                    <p className="mt-1"><strong>Confidence:</strong> {Math.round(selectedTicket.ai_confidence * 100)}%</p>
                    {selectedTicket.similar_ticket_count > 0 && (
                      <p className="mt-1"><strong>Similar requests:</strong> {selectedTicket.similar_ticket_count}</p>
                    )}
                  </div>
                </div>
                
                {/* AI Draft Response */}
                {selectedTicket.ai_draft_response && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Bot className="h-4 w-4 text-purple-600" />
                      AI Drafted Response
                      <Badge variant="outline" className="text-xs">Editable</Badge>
                    </label>
                    <Textarea
                      value={editedResponse}
                      onChange={(e) => setEditedResponse(e.target.value)}
                      rows={5}
                      className="mt-1"
                      data-testid="ai-response-editor"
                    />
                  </div>
                )}
                
                {/* Linked Roadmap Item */}
                {linkedRoadmap && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-blue-700 font-medium">
                        <Lightbulb className="h-4 w-4" />
                        Auto-Created Roadmap Item
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleKillRoadmap(linkedRoadmap.id)}
                        data-testid="kill-roadmap-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Kill Switch
                      </Button>
                    </div>
                    <div className="mt-2 text-sm">
                      <p><strong>Title:</strong> {linkedRoadmap.title}</p>
                      <p><strong>Status:</strong> {linkedRoadmap.status}</p>
                      <p><strong>Priority:</strong> {linkedRoadmap.priority}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="flex gap-2 flex-col sm:flex-row">
                {/* Status Change Dropdown - Always visible */}
                <div className="flex items-center gap-2 mr-auto">
                  <Label className="text-sm text-gray-500">Change Status:</Label>
                  <Select 
                    value={selectedTicket.status} 
                    onValueChange={handleStatusChange}
                    data-testid="status-change-select"
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="Drafted">Drafted</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Resolved">Resolved</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedTicket.status === 'Drafted' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleReject}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject AI Response
                    </Button>
                    
                    {linkedRoadmap ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove('Planned')}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="approve-plan-feature-btn"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve & Plan Feature
                        </Button>
                        <Button
                          onClick={() => handleApprove('Rejected')}
                          variant="outline"
                        >
                          Approve & Reject Feature
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleApprove()}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid="approve-sync-btn"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Approve & Resolve
                      </Button>
                    )}
                  </>
                )}
                
                {selectedTicket.status === 'In Progress' && (
                  <div className="text-blue-600 flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Feature In Progress
                  </div>
                )}
                
                {selectedTicket.status === 'Resolved' && (
                  <div className="text-green-600 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Ticket Resolved
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Create New Ticket
            </DialogTitle>
            <DialogDescription>
              Submit a new support ticket. AI will automatically classify it and draft a response.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                placeholder="Brief description of the issue or request"
                value={newTicket.subject}
                onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                data-testid="new-ticket-subject"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                placeholder="Provide detailed information about your issue, feature request, or question..."
                rows={5}
                value={newTicket.message}
                onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                data-testid="new-ticket-message"
              />
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-purple-700 text-sm">
                <Bot className="h-4 w-4" />
                <span className="font-medium">AI will automatically:</span>
              </div>
              <ul className="mt-2 text-sm text-purple-600 space-y-1 ml-6">
                <li>• Classify as Bug, Feature, or Question</li>
                <li>• Assign priority based on content</li>
                <li>• Draft a professional response</li>
                <li>• Create roadmap item for feature requests</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTicket}
              disabled={isSubmitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-new-ticket"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Create & Classify
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
